import { useSyncExternalStore } from "react";
import type { Report } from "../lib/api/index.js";
import { PATHS, reportScreenPath, SCREEN_NAMES, type ScreenKind } from "../lib/routes.js";
import { AppLink } from "./app-link.js";
import "./appbar/appbar.css";
import { Lens } from "./appbar/lens.js";
import { RunPill } from "./appbar/run-pill.js";
import { ChevronRight, CircleHelp } from "./icons.js";
import { PaletteTrigger } from "./palette/palette-trigger.js";
import { ExportButton, exportKind } from "./report-export/export-button.js";
import { useTour } from "./tour/tour.js";

/**
 * The bar across the top of every signed-in page (DESIGN.md §5).
 *
 * Left, on a brand's measuring screens, the lens: the dates, the engine and
 * the market every number below is narrowed to. On a screen inside another,
 * the way back to it. Right, the brand's latest run, the export, Jump to and
 * the tour.
 */

/** The screens whose numbers the lens narrows. Settings measures nothing. */
const MEASURING: ReadonlySet<ScreenKind> = new Set([
  "overview",
  "prompts",
  "rivals",
  "citations",
  "offPage",
]);

/** Screens that live inside another, and the one they go back to. */
const PARENT: Partial<Record<ScreenKind, "library" | "fanout">> = {
  addPrompts: "library",
  "fanout-study": "fanout",
};

export function TopBar({
  screen,
  report,
  workspaceId,
  today,
}: {
  readonly screen: ScreenKind;
  /** The report on screen, when this screen is about one. */
  readonly report: Report | null;
  /** Null before a workspace is chosen; the export waits for one. */
  readonly workspaceId: string | null;
  /** Injected, so the export's presets are a value rather than a read of the clock. */
  readonly today: Date;
}) {
  const aboutReport = reportScreenPath(screen) !== null ? report : null;
  const exporting = exportKind(screen);
  const parent = PARENT[screen];
  const scrolled = useScrolled();
  return (
    <header className="appbar" data-scrolled={scrolled ? "" : undefined}>
      <div className="appbar-start">
        {aboutReport !== null && MEASURING.has(screen) ? (
          <Lens report={aboutReport} today={today} />
        ) : parent === undefined ? null : (
          <nav aria-label="Breadcrumb">
            <ol className="appbar-crumbs">
              <li>
                <AppLink className="appbar-crumb-link" href={PATHS[parent]}>
                  {SCREEN_NAMES[parent]}
                </AppLink>
                <ChevronRight className="appbar-crumb-sep" aria-hidden="true" />
              </li>
              <li aria-current="page">{SCREEN_NAMES[screen]}</li>
            </ol>
          </nav>
        )}
      </div>
      <div className="appbar-tools">
        {aboutReport !== null && workspaceId !== null ? (
          <RunPill workspaceId={workspaceId} report={aboutReport} />
        ) : null}
        {aboutReport !== null && workspaceId !== null && exporting !== null ? (
          <ExportButton
            kind={exporting}
            workspaceId={workspaceId}
            report={aboutReport}
            today={today}
          />
        ) : null}
        <PaletteTrigger />
        <TourButton />
      </div>
    </header>
  );
}

function TourButton() {
  const tour = useTour();
  return (
    <button
      type="button"
      className="appbar-help"
      aria-label="Take the tour"
      title="Take the tour"
      onClick={tour.start}
    >
      <CircleHelp className="appbar-help-icon" aria-hidden="true" />
    </button>
  );
}

/*
 * Whether the page has scrolled under the bar: its hairline shows only then
 * (DESIGN.md §5). The window's scroll is external state, so it is read the way
 * the address is, not copied into an effect.
 */
function subscribeScroll(onChange: () => void): () => void {
  window.addEventListener("scroll", onChange, { passive: true });
  return () => {
    window.removeEventListener("scroll", onChange);
  };
}

function useScrolled(): boolean {
  return useSyncExternalStore(
    subscribeScroll,
    () => window.scrollY > 4,
    () => false,
  );
}
